<!--
// app/components/pages/mercato/TeamsSection.vue
-->
<script setup lang="ts">
import { setAliasUrl } from "~~/src/Shared/utils";
import type { MercatoPlayer } from "~~/src/mercato/domain/models/MercatoPlayer";
import type { MercatoTeam } from "~~/src/mercato/domain/models/MercatoTeam";
import BaseModal from "~/components/base/BaseModal.vue";

type Props = {
  teams: MercatoTeam[];
  league: string;
};

const props = defineProps<Props>();

const { $t, $tn } = useI18n();

const { playerPlaceholderPath } = useImages();

// Fetch all market players
const { data: allMarketPlayers } = useNuxtData("mercatoMarketPlayers");
if (!allMarketPlayers.value) console.error("Error getting market players");

// Modal state
const isModalOpen = ref(false);
const selectedTeam = ref<MercatoTeam | null>(null);

// Pagination state (for PrimePaginator, 0-indexed)
const currentPage = ref(0); // PrimeVue uses 0-indexed first record
const rowsPerPage = ref(12);

// Filter teams by league and sort alphabetically
const leagueTeamsWithBudget = computed(() => {
  return props.teams
    .filter(team => {
      // Only filter by league if not "all" (league and budget already filtered at server)
      if (props.league !== "all") return team.league === props.league;
      return team.id != 0 && team.id != 500;
    })
    .sort((a, b) => a.completeName.localeCompare(b.completeName));
});

// Pre-compute team player counts and players for performance
const teamPlayersMap = computed(() => {
  if (!allMarketPlayers.value) return new Map<number, MercatoPlayer[]>();

  const map = new Map<number, MercatoPlayer[]>();

  // Group players by teamId
  allMarketPlayers.value.forEach(player => {
    const teamId = parseInt(player.teamId);
    if (!map.has(teamId)) {
      map.set(teamId, []);
    }
    map.get(teamId)!.push(player);
  });

  return map;
});

// Get player count for a team
const getTeamPlayerCount = (teamId: number): number => {
  return teamPlayersMap.value.get(teamId)?.length || 0;
};

// Get players for a team
const getTeamPlayers = (teamId: number): MercatoPlayer[] => {
  return teamPlayersMap.value.get(teamId) || [];
};

// Check if team is clickable (has players)
const isTeamClickable = (teamId: number): boolean => {
  return getTeamPlayerCount(teamId) > 0;
};

// Calculate salary cap percentage
const getSalaryCapPercentage = (budget: number): number => {
  return Math.round((budget / 100) * 100); // budget is already a percentage
};

// Get salary cap color based on budget percentage
const getSalaryCapColor = (budget: number): string => {
  if (Math.round(budget) < 80) return "#00C94A"; // Green - Under cap
  if (Math.round(budget) <= 100) return "#F48423"; // Orange - Near/At cap
  return "#D83737"; // Red - Over cap (Luxury)
};

// Open modal with team details
const openTeamModal = (team: MercatoTeam) => {
  if (!isTeamClickable(team.id)) return;
  selectedTeam.value = team;
  isModalOpen.value = true;
};

// Close modal
const closeModal = () => {
  isModalOpen.value = false;
  selectedTeam.value = null;
};

// Get selected team players
const selectedTeamPlayers = computed(() => {
  if (!selectedTeam.value) return [];
  return getTeamPlayers(selectedTeam.value.id);
});

// Computed: Teams to display (with pagination when league === 'all')
const displayedTeams = computed(() => {
  if (props.league !== "all") {
    // No pagination for specific leagues
    return leagueTeamsWithBudget.value;
  }

  // Apply pagination for 'all' leagues
  const start = currentPage.value;
  const end = start + rowsPerPage.value;
  return leagueTeamsWithBudget.value.slice(start, end);
});

// Handle pagination change event
const onPageChange = (event: { first: number; rows: number }) => {
  currentPage.value = event.first;
  rowsPerPage.value = event.rows;
};

// Watch league changes to reset pagination
watch(
  () => props.league,
  () => {
    currentPage.value = 0;
    rowsPerPage.value = 12;
  }
);
</script>

<template>
  <section ref="teams" id="teams" class="kql-w-container px-4 xl:px-0 my-10">
    <!-- Team Cards Grid -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      <div
        v-for="team in displayedTeams"
        :key="team.id"
        :class="[
          'team-card',
          {
            'team-card--clickable': isTeamClickable(team.id),
            'team-card--disabled': !isTeamClickable(team.id),
          },
        ]"
        @click="openTeamModal(team)"
      >
        <!-- Team Logo -->
        <div class="team-card__info">
          <NuxtImg
            :src="setAliasUrl(team.logo.url)"
            :alt="team.completeName"
            width="60"
            height="60"
            sizes="60px"
            class="rounded-lg transition-all duration-300 ease-in-out"
          />

          <!-- Team Name -->
          <div class="team-card__header">
            <h3 class="team-card__name">{{ team.completeName }}</h3>
            <p class="team-card__players-count">
              {{ getTeamPlayerCount(team.id) }} {{ $t("menuItem.players") }}
            </p>
          </div>
        </div>

        <!-- Team Info -->
        <div class="team-card__content">
          <!-- Salary Cap -->
          <div class="team-card__salary-cap">
            <div class="flex items-center justify-between mb-1">
              <span class="text-xs text-gray-400">{{ $t("mercato.salaryCapUsage") }}</span>
              <span class="text-xs font-bold text-gray-400">{{ Math.round(team.budget) }}%</span>
            </div>
            <!-- Progress Bar -->
            <div class="salary-cap-bar">
              <div
                class="salary-cap-bar__fill"
                :style="{
                  width: `${Math.min(getSalaryCapPercentage(team.budget || 0), 100)}%`,
                  backgroundColor: getSalaryCapColor(team.budget || 0),
                }"
              ></div>
            </div>

            <div class="flex items-center justify-between mt-1">
              <span class="text-xs" :style="{ color: getSalaryCapColor(team.budget || 0) }">{{
                getSalaryCapPercentage(team.budget || 0) > 100
                  ? "Luxury Tax"
                  : getSalaryCapPercentage(team.budget || 0) === 100
                    ? ""
                    : "Under Cap"
              }}</span>
              <span class="text-xs font-bold" :style="{ color: getSalaryCapColor(team.budget || 0) }"
                >{{ getSalaryCapPercentage(team.budget || 0) - 100 }}%</span
              >
            </div>
          </div>
        </div>
      </div>
    </div>

    <PrimePaginator
      v-if="league === 'all'"
      v-model:first="currentPage"
      v-model:rows="rowsPerPage"
      class="mt-6"
      :totalRecords="leagueTeamsWithBudget.length"
      :rowsPerPageOptions="[12, 24, 42]"
      @page="onPageChange"
    ></PrimePaginator>

    <!-- Team Players Modal -->
    <BaseModal modal-id="teamPlayerModal" v-model="isModalOpen" show-close-button>
      <!-- Modal Header -->
      <template #header>
        <div class="team-modal__header">
          <div class="flex items-center gap-4">
            <NuxtImg
              :src="setAliasUrl(selectedTeam.logo.url)"
              :alt="selectedTeam.completeName"
              width="60"
              height="60"
              sizes="60px"
              class="rounded-lg"
            />
            <div>
              <h2 class="text-2xl font-bold">{{ selectedTeam.completeName }}</h2>
              <p class="text-gray-400">{{ selectedTeamPlayers.length }} {{ $t("menuItem.players") }}</p>
            </div>
          </div>
        </div>
      </template>

      <!-- Players Grid -->
      <template #body>
        <div class="team-modal__players">
          <div v-for="player in selectedTeamPlayers" :key="player.id" class="player-card">
            <!-- Player Image -->
            <div class="player-card__image">
              <NuxtImg
                :src="setAliasUrl(player.imageUrl) || playerPlaceholderPath"
                :alt="player.name"
                width="48"
                height="48"
                sizes="48px"
                class="rounded-full object-cover object-top bg-gray-700 w-12 h-12"
                fit="outside"
              />
            </div>

            <!-- Player Info -->
            <div class="player-card__info">
              <p class="player-card__name">{{ player.name }}</p>
              <p class="player-card__position">{{ $t(player.position) }}</p>
              <p class="player-card__market-value">
                {{ $tn(Number(player.marketValue)) }} <span class="text-xs uppercase">M</span>
              </p>
            </div>
          </div>
        </div>
      </template>
    </BaseModal>
  </section>
</template>

<style scoped>
/* Team Card Styles */
.team-card {
  @apply bg-gray-800 rounded-lg p-4 border border-gray-800 transition-all duration-300 ease-in-out;
}

.team-card--clickable {
  @apply cursor-pointer;
}

.team-card--clickable:hover {
  @apply border-mercato;
  /*transform: translateY(-4px);*/
  box-shadow:
    0 0 20px rgba(0, 201, 74, 0.3),
    0 4px 12px rgba(0, 0, 0, 0.2);
  background: linear-gradient(to bottom, rgba(0, 201, 74, 0.1), transparent);
}

.team-card--disabled {
  @apply cursor-not-allowed;
}

.team-card__info {
  @apply flex items-center justify-start gap-2 w-full;
}

.team-card:hover .team-card__info img,
.team-card:hover .team-card__name {
  @apply scale-110 origin-bottom-left;
}

.team-card__content {
  @apply flex-1 min-w-0;
}

.team-card__header {
  @apply mb-2;
}

.team-card__name {
  @apply text-lg font-bold transition-all duration-300 ease-in-out;
}

.team-card__players-count {
  @apply text-sm text-gray-400 lowercase;
}

.team-card__salary-cap {
  @apply mt-2;
}

/* Salary Cap Progress Bar */
.salary-cap-bar {
  @apply w-full h-2 bg-gray-700 rounded-full overflow-hidden;
}

.salary-cap-bar__fill {
  @apply h-full transition-all duration-300 rounded-full;
}

/* Team Modal Styles */

.team-modal__header {
  @apply mb-6 pb-4 border-b border-gray-700;
}

.team-modal__players {
  @apply grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 min-w-max max-h-[60vh] sm:max-h-none;
  @apply -mt-4 overflow-y-auto;
}

/* Player Card Styles */
.player-card {
  @apply bg-gray-800 rounded-lg p-3 flex items-center justify-start max-w-full min-w-min gap-3;
}

.player-card__image {
  @apply flex-shrink-0;
}

.player-card__info {
  @apply flex-1 min-w-[200px] max-w-[200px];
}

.player-card__name {
  @apply font-bold text-sm mb-1;
}

.player-card__position {
  @apply text-xs text-gray-400 mb-1;
}

.player-card__market-value {
  @apply text-sm text-primary font-semibold;
}
</style>
